import { useState, useEffect } from "react";
import { MapPin } from "lucide-react";
import { REALISATION_CATEGORIES, PLACEHOLDER_REALISATIONS } from "../data/content";
import { useSeo, Reveal } from "../lib/motion";
import { Breadcrumb, CTASection } from "../components/CTASection";
import { api, fileUrl } from "../lib/api";

const catLabel = (k) => REALISATION_CATEGORIES.find((c) => c.key === k)?.label || k;

export default function Realisations() {
  useSeo("Nos réalisations", "Découvrez les réalisations de HP Froid & Climatisation en Alsace : climatisation, gainable, froid commercial et chambres froides.");
  const [filter, setFilter] = useState("toutes");
  const [items, setItems] = useState([]);
  const [usingPlaceholder, setUsingPlaceholder] = useState(false);

  useEffect(() => {
    api.get("/realisations").then(({ data }) => {
      if (data.length === 0) { setItems(PLACEHOLDER_REALISATIONS); setUsingPlaceholder(true); }
      else { setItems(data); setUsingPlaceholder(false); }
    }).catch(() => { setItems(PLACEHOLDER_REALISATIONS); setUsingPlaceholder(true); });
  }, []);

  const filtered = filter === "toutes" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Réalisations" }]} />
        <h1 className="font-heading font-black tracking-tight text-4xl md:text-6xl text-ink mt-8">Nos réalisations</h1>
        <p className="text-maintext text-lg mt-5 max-w-2xl">Un aperçu de nos installations de climatisation et de froid en Alsace.</p>
        {usingPlaceholder && (
          <p className="text-sm text-muted2 bg-cloud border border-line rounded-md px-4 py-3 mt-6 inline-block">Exemples d’illustration — les réalisations réelles seront ajoutées depuis l’administration.</p>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-28">
        <div className="flex flex-wrap gap-2 mb-10">
          {REALISATION_CATEGORIES.map((c) => (
            <button key={c.key} data-testid={`filter-${c.key}`} onClick={() => setFilter(c.key)}
              className={`px-5 py-2.5 rounded-md font-semibold text-sm transition-[background-color,color,border-color] border ${filter === c.key ? "bg-ink text-white border-ink" : "bg-white text-maintext border-line hover:border-ice/40"}`}>
              {c.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="text-muted2 py-20 text-center">Aucune réalisation dans cette catégorie pour le moment.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.08}>
                <article data-testid={`realisation-${r.id}`} className="group bg-white border border-line rounded-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="clip-frame aspect-[4/3] relative">
                    <img src={fileUrl(r.images?.[0])} alt={r.title} className="w-full h-full object-cover" />
                    <span className="absolute top-3 left-3 text-xs font-bold text-white bg-ink/70 backdrop-blur px-2.5 py-1 rounded">{catLabel(r.category)}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-xl text-ink">{r.title}</h3>
                    {r.city && <p className="text-sm text-muted2 mt-1.5 flex items-center gap-1.5"><MapPin size={14} className="text-ice" /> {r.city}</p>}
                    {r.description && <p className="text-maintext text-sm mt-3 leading-relaxed">{r.description}</p>}
                    {r.material && <p className="text-xs text-muted2 mt-3 font-mono">Matériel : {r.material}</p>}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </section>
      <CTASection />
    </div>
  );
}
